<script setup>
import { computed } from "vue";
import { getArticle } from "../data/articles";
import { localeInfo, siteCopy } from "../data/content";
import IconArrow from "../components/IconArrow.vue";
import SiteFooter from "../components/SiteFooter.vue";
import SiteHeader from "../components/SiteHeader.vue";

const props = defineProps({
  locale: { type: String, required: true },
  slug: { type: String, required: true },
});

const article = computed(() => getArticle(props.slug));
const content = computed(() => article.value.content[props.locale]);
const site = computed(() => siteCopy[props.locale]);
const blogCopy = computed(() => site.value.blog);
const contactLabel = computed(() => site.value.nav.contact);
const path = computed(() => `/blog/${article.value.slug}`);

const schemaJson = computed(() => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: content.value.title,
  description: content.value.summary,
  image: `https://corpresi.com${article.value.image}`,
  datePublished: article.value.publishedAt,
  dateModified: article.value.updatedAt,
  inLanguage: props.locale,
  author: { "@type": "Organization", name: "Resi Corporation" },
  publisher: { "@type": "Organization", name: "Resi Corporation" },
  mainEntityOfPage: `https://corpresi.com/${props.locale}/blog/${article.value.slug}`,
}));

function formatDate(value) {
  return new Intl.DateTimeFormat(localeInfo[props.locale].htmlLang, { dateStyle: "long" })
    .format(new Date(`${value}T12:00:00`));
}

function contentsLabel() {
  if (props.locale === "en") return "Contents";
  if (props.locale === "it") return "Contenuti";
  return "Contenido";
}
</script>

<template>
  <div v-if="article" class="site-page inner-page" :lang="locale">
    <SiteHeader :locale="locale" :nav="site.nav" :path="path" />
    <main id="main">
      <article class="article-page">
        <header class="article-hero">
          <div class="shell article-hero__grid">
            <div>
              <RouterLink class="article-back" :to="`/${locale}/blog`">← {{ blogCopy.kicker }}</RouterLink>
              <p class="eyebrow">{{ article.readingMinutes }} {{ blogCopy.minutes }}</p>
              <h1>{{ content.title }}</h1>
              <p class="article-summary">{{ content.summary }}</p>
              <div class="article-dates">
                <span>Resi Corporation</span>
                <time :datetime="article.updatedAt">{{ formatDate(article.updatedAt) }}</time>
              </div>
            </div>
            <div class="article-hero__image">
              <img :src="article.image" alt="" width="960" height="600" fetchpriority="high">
            </div>
          </div>
        </header>

        <div class="shell article-layout">
          <aside class="article-aside">
            <span>{{ contentsLabel() }}</span>
            <a
              v-for="(section, index) in content.sections"
              :key="section.heading"
              :href="`#section-${index + 1}`"
            >
              {{ String(index + 1).padStart(2, "0") }} {{ section.heading }}
            </a>
          </aside>
          <div class="article-body">
            <section
              v-for="(section, index) in content.sections"
              :id="`section-${index + 1}`"
              :key="section.heading"
              data-reveal
            >
              <span class="article-section-number">{{ String(index + 1).padStart(2, "0") }}</span>
              <h2>{{ section.heading }}</h2>
              <p v-for="paragraph in section.paragraphs || []" :key="paragraph">{{ paragraph }}</p>
              <ul v-if="section.bullets">
                <li v-for="bullet in section.bullets" :key="bullet">{{ bullet }}</li>
              </ul>
            </section>
            <div class="article-cta" data-reveal>
              <span>{{ contactLabel }}</span>
              <h2>{{ content.cta }}</h2>
              <RouterLink class="button button--primary" :to="`/${locale}#contact`">
                {{ contactLabel }}<IconArrow />
              </RouterLink>
            </div>
          </div>
        </div>
        <component :is="'script'" type="application/ld+json" v-html="schemaJson" />
      </article>
    </main>
    <SiteFooter :locale="locale" :copy="site.footer" />
  </div>
</template>
